import React, {ChangeEvent, useEffect, useMemo, useState} from "react";
import Title from "@components/title";
import AnimatedPage from "@components/animated-page";
import {
    Badge,
    Box,
    Button,
    Container,
    Flex,
    GridItem,
    Heading,
    Icon,
    Select as MySelect,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import {SingleDatepicker} from "@components/date-picker";
import TimePicker from "@components/time-picker";
import {useDispatch} from "react-redux";
import {AppDispatch, RootState} from "@src/store";
import {
    getOpeningTimes,
    getReservationSettings,
    getSites,
    getSitesWorkplace,
} from "@features/reservation/action";
import {fetchAvailableMaterials, createMaterialReservation} from "@features/material/action";
import {clearMaterials} from "@features/material/slice";
import {useAppSelector} from "@store/hooks";
import {http} from "@utils/http";
import moment from "moment/moment";
import {useTranslation} from "react-i18next";
import {formatDate} from "@helpers/helpers";
import useTitle from "@hooks/useTitle";
import i18n from "i18next";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {FiCheck} from "react-icons/fi";
import {MaterialType} from "@features/material/types";

const CreateMaterialReservation = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {t} = useTranslation();
    const lang = i18n.language;
    const navigate = useNavigate();

    const {sites, sitesWorkplace, openingTimes, reservationSettings} = useAppSelector(
        (state: RootState) => state.reservation
    );

    const allSites = useMemo(() => {
        const seen = new Set<any>();
        return [...sites, ...sitesWorkplace].filter(s => {
            if (seen.has(s.ID)) return false;
            seen.add(s.ID);
            return true;
        });
    }, [sites, sitesWorkplace]);

    const [sitesWithMaterials, setSitesWithMaterials] = useState<Set<any> | null>(null);

    useEffect(() => {
        if (!allSites.length) return;
        setSitesWithMaterials(null);
        const today = formatDate(new Date());
        Promise.all(
            allSites.map((site: any) =>
                http.get('/customerportal/availableMaterials/', {
                    params: {SiteID: site.ID, ResStart: today + ' 00:00', ResStop: today + ' 23:59', RequiresReservation: 0},
                })
                .then(r => ({id: site.ID, has: Array.isArray(r.data) && r.data.length > 0}))
                .catch(() => ({id: site.ID, has: true}))
            )
        ).then(results => {
            setSitesWithMaterials(new Set(results.filter(r => r.has).map(r => r.id)));
        });
    }, [allSites]);

    const filteredSites = useMemo(() =>
        sitesWithMaterials ? allSites.filter(s => sitesWithMaterials.has(s.ID)) : allSites,
    [allSites, sitesWithMaterials]);

    const {availableMaterials, isLoading: materialsLoading} = useAppSelector(
        (state: RootState) => state.material
    );
    const clientID: number = useAppSelector(
        (state: RootState) => (state as any).user?.userOptions?.id ?? 0
    );

    const {reservationBlockSize, maximumReservationTime} = reservationSettings;

    const [date, setDate] = useState(new Date());
    const [siteID, setSiteID] = useState<string | null>(null);
    const [startTimeArray, setStartTimeArray] = useState<any>();
    const [stopTimeArray, setStopTimeArray] = useState<any>();
    const [selectedStartTime, setSelectedStartTime] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState('');
    const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
    const [searched, setSearched] = useState(false);
    const [confirming, setConfirming] = useState(false);

    useTitle(t("documentTitle.reservationManagement"));

    useEffect(() => {
        dispatch(getSites());
        dispatch(getSitesWorkplace());
        return () => {
            dispatch(clearMaterials());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!siteID) return;
        setSelectedStartTime('');
        setSelectedEndTime('');
        setSelectedMaterialIds([]);
        setSearched(false);
        dispatch(clearMaterials());
        dispatch(getOpeningTimes({siteID}));
        dispatch(getReservationSettings({siteID}));
    }, [dispatch, siteID]);

    useEffect(() => {
        if (filteredSites.length && !siteID) {
            setSiteID(filteredSites[0]?.ID);
        }
    }, [filteredSites, siteID]);

    let timeArr: any = [];

    function getRoundedDate(minutes: any, d = new Date()) {
        const ms = 1000 * 60 * minutes;
        return new Date(Math.floor(d.getTime() / ms) * ms);
    }

    const timesList = (date: any, start: any, end: any, minutes_step: number) => {
        const roundedCurrentDateTime = getRoundedDate(reservationBlockSize, new Date());
        const todayStart = moment(
            formatDate(roundedCurrentDateTime) + ' ' +
            roundedCurrentDateTime.getHours().toString().padStart(2, '0') + ':' +
            roundedCurrentDateTime.getMinutes().toString().padStart(2, '0')
        );
        const dateStart = moment(formatDate(date) + ' ' + start);
        const dateEnd = moment(formatDate(date) + ' ' + end);
        const remainder = dateEnd.minute() % reservationBlockSize;
        const blockEnd = moment(dateEnd).subtract(remainder, "minutes").format("HH:mm");

        if (todayStart.format("YYYY-MM-DD") === dateStart.format("YYYY-MM-DD") && todayStart.format("HH:mm") >= blockEnd) {
            return;
        }
        if (todayStart.format("YYYY-MM-DD") === dateStart.format("YYYY-MM-DD") && todayStart.format("HH:mm") > dateStart.format("HH:mm") && todayStart.format("HH:mm") < blockEnd) {
            timeArr.push(moment(todayStart).format("HH:mm"));
            while (timeArr[timeArr.length - 1] < blockEnd) {
                timeArr.push(todayStart.add(minutes_step, 'minutes').format("HH:mm"));
            }
        } else {
            timeArr.push(dateStart.format("HH:mm"));
            while (timeArr[timeArr.length - 1] < blockEnd) {
                timeArr.push(dateStart.add(minutes_step, 'minutes').format("HH:mm"));
            }
        }
    };

    const padTime = (t: string) => t.split(':').slice(0, 2).map(s => s.padStart(2, '0')).join(':');

    const effectiveStopTimeArray = useMemo(() => {
        if (!selectedStartTime || !stopTimeArray?.length || !openingTimes?.length) return stopTimeArray;
        const todayTimes = openingTimes.find((item: any) => item.date === formatDate(date));
        if (!todayTimes) return stopTimeArray;
        const block = todayTimes.openTimes.find((b: any) => {
            if (!b) return false;
            return selectedStartTime >= padTime(b.startTime) && selectedStartTime < padTime(b.stopTime);
        });
        if (!block) return stopTimeArray;
        const maxStop = padTime(block.stopTime);
        return stopTimeArray.filter((t: string) => t <= maxStop);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStartTime, stopTimeArray, openingTimes, date]);

    useEffect(() => {
        if (!reservationBlockSize || !siteID || !openingTimes?.length) return;
        timeArr = [];
        const todayEntry = openingTimes.find((item: any) => item.date === formatDate(date));
        if (!todayEntry) return;
        if (todayEntry.openTimes.every((v: any) => v !== null)) {
            todayEntry.openTimes.filter((val: any) => val !== null).forEach(({startTime, stopTime}: any) => {
                timesList(
                    date,
                    startTime.split(':')[0].padStart(2, '0') + ':' + startTime.split(':')[1].padStart(2, '0'),
                    stopTime.split(':')[0].padStart(2, '0') + ':' + stopTime.split(':')[1].padStart(2, '0'),
                    reservationBlockSize
                );
            });
            const stopTimeArr = todayEntry.openTimes.filter((val: any) => val !== null).map(({stopTime}: any) =>
                stopTime.split(':')[0].padStart(2, '0') + ':' + stopTime.split(':')[1].padStart(2, '0')
            );
            setStartTimeArray(timeArr.filter((el: any) => stopTimeArr.indexOf(el) < 0 && el !== "23:45"));
            setStopTimeArray(timeArr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteID, openingTimes, reservationBlockSize, date]);

    const filterOpenDays = openingTimes?.filter((f: any) => f.openTimes?.some((o: any) => o?.startTime || o?.stopTime));

    const filteredClosingDays = openingTimes.length
        ? openingTimes.filter((f: any) => f.openTimes?.some((o: any) => !o?.startTime || !o?.stopTime))
        : [];
    const closingDays = filteredClosingDays.map((f: any) =>
        new Date(moment(f.date).format('YYYY-MM-DD')).getTime()
    );

    const handleSite = (event: ChangeEvent<HTMLSelectElement>) => {
        setSiteID(event.target.value);
    };

    const search = async () => {
        if (!siteID || !selectedStartTime || !selectedEndTime) return;
        setSelectedMaterialIds([]);
        setSearched(false);
        await dispatch(fetchAvailableMaterials({
            siteID: Number(siteID),
            resStart: formatDate(date) + ' ' + selectedStartTime,
            resStop: formatDate(date) + ' ' + selectedEndTime,
            requiresReservation: false,
        }));
        setSearched(true);
    };

    const toggle = (id: number) => {
        setSelectedMaterialIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleConfirm = async () => {
        if (selectedMaterialIds.length === 0) return;
        setConfirming(true);
        try {
            for (const id of selectedMaterialIds) {
                const material = availableMaterials.find((m: MaterialType) => m.materialTypeId === id)!;
                await dispatch(createMaterialReservation({
                    materialTypeID: material.materialTypeId,
                    clientID,
                    quantityReserved: 1,
                    resStart: formatDate(date) + ' ' + selectedStartTime,
                    resStop: formatDate(date) + ' ' + selectedEndTime,
                })).unwrap();
            }
            toast.success(t("createMaterialReservation.success"));
            navigate(`/${lang}/${t("router.mijn-reservaties")}`);
        } catch {
            // 409 errors are handled globally in http.ts
        } finally {
            setConfirming(false);
        }
    };

    return (
        <>
            <Title title={t("createMaterialReservation.title")}/>
            <AnimatedPage animationType={"FadeIn"}>
                <Container maxW={"container.xl"} p={{base: "22px 16px", md: "45px 32px"}}>
                    <SimpleGrid
                        bg={"custom.gray_100"}
                        padding={"32px"}
                        columns={{base: 1}}
                        alignItems={"flex-start"}
                        gap={"16px"}
                        mb={"45px"}
                    >
                        <SimpleGrid columns={{base: 1, md: 4}} gap={"20px"}>
                            <GridItem>
                                <SingleDatepicker
                                    name="date-input"
                                    date={date}
                                    onDateChange={(d: Date) => {
                                        setDate(d);
                                        setSelectedStartTime('');
                                        setSelectedEndTime('');
                                        setSearched(false);
                                        dispatch(clearMaterials());
                                    }}
                                    disabled={!filterOpenDays.length}
                                    maxDate={openingTimes[openingTimes?.length - 1]?.date}
                                    closingDays={closingDays}
                                />
                            </GridItem>
                            <GridItem>
                                <MySelect variant={"black"} width={"auto"} onChange={handleSite}>
                                    {filteredSites.map(({label, ID}: any, index: number) => (
                                        <option key={index} value={ID}>{label}</option>
                                    ))}
                                </MySelect>
                            </GridItem>
                            <GridItem>
                                <TimePicker
                                    startTimeArray={startTimeArray}
                                    stopTimeArray={effectiveStopTimeArray}
                                    inputPlaceholder={t('newReservation.search.selectTime.placeholder')}
                                    setSelectedStartTime={setSelectedStartTime}
                                    selectedStartTime={selectedStartTime}
                                    setSelectedEndTime={setSelectedEndTime}
                                    selectedEndTime={selectedEndTime}
                                    disabled={!siteID}
                                    reservationBlockSize={reservationBlockSize}
                                    maximumReservationTime={maximumReservationTime}
                                />
                            </GridItem>
                            <GridItem>
                                <Button
                                    variant={"primary"}
                                    width={"100%"}
                                    onClick={search}
                                    isDisabled={!siteID || !selectedStartTime || !selectedEndTime}
                                >
                                    {t("createMaterialReservation.search.btn")}
                                </Button>
                            </GridItem>
                        </SimpleGrid>
                    </SimpleGrid>

                    {materialsLoading ? (
                        <Spinner/>
                    ) : searched && (
                        <>
                            {availableMaterials.length === 0 ? (
                                <Text color={"custom.gray_500"}>{t("createMaterialReservation.noMaterialsAvailable")}</Text>
                            ) : (
                                <>
                                    <Stack spacing={3} mb={6}>
                                        {availableMaterials.map((material: MaterialType) => {
                                            const isSelected = selectedMaterialIds.includes(material.materialTypeId);
                                            return (
                                                <Box
                                                    key={material.materialTypeId}
                                                    borderWidth={isSelected ? 2 : 1}
                                                    borderRadius={"md"}
                                                    p={4}
                                                    cursor={"pointer"}
                                                    borderColor={isSelected ? "custom.brand_primary" : "custom.gray_300"}
                                                    bg={isSelected ? "custom.gray_100" : "white"}
                                                    onClick={() => toggle(material.materialTypeId)}
                                                >
                                                    <Flex justify={"space-between"} align={"flex-start"}>
                                                        <Box>
                                                            <Flex align={"center"} gap={2}>
                                                                <Heading fontSize={"md"} fontWeight={"semibold"}>
                                                                    {material.name}
                                                                </Heading>
                                                                {isSelected && (
                                                                    <Icon as={FiCheck} color={"custom.brand_primary"} boxSize={4}/>
                                                                )}
                                                            </Flex>
                                                            <Text fontSize={"sm"} color={"gray.600"}>
                                                                {material.storageLocation}
                                                            </Text>
                                                            {material.description && (
                                                                <Text fontSize={"sm"} color={"gray.500"} mt={1}>
                                                                    {material.description}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                        <Box textAlign={"right"}>
                                                            <Badge colorScheme={"green"} mb={1}>
                                                                {material.availableQuantity} {t("materialOffer.available")}
                                                            </Badge>
                                                            <Text fontWeight={"bold"} fontSize={"sm"}>
                                                                {material.isPaid && material.pricePerUnit != null
                                                                    ? `€ ${material.pricePerUnit.toFixed(2)}`
                                                                    : t("materialOffer.free")}
                                                            </Text>
                                                        </Box>
                                                    </Flex>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                    <Button
                                        variant={"primary"}
                                        onClick={handleConfirm}
                                        isLoading={confirming}
                                        isDisabled={confirming || selectedMaterialIds.length === 0}
                                    >
                                        {t("createMaterialReservation.confirm.btn")}
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </Container>
            </AnimatedPage>
        </>
    );
};

export default CreateMaterialReservation;
